import React, { Fragment, useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { Button } from 'react-bootstrap';
import classNames from 'classnames';
import PropTypes from 'prop-types';

import { requestAnalysisQualityProduct } from 'api/sfms/report';
import List from 'components/common-sfims/List';
import Modal from 'components/common-sfims/modal/Common';
import { getText } from 'function/language';
import { RESPONSE_CODE } from 'types/const';
import comma from 'utils/format/comma';

/**
 * 리포트 > 품질 결과 분석 > 제품 - 분석
 */
const Analysis = ({ show, prdtCd, analysisType, onHide }) => {
  /* router */
  const { reportYm, factoryCodeSeq } = useParams();

  /* state */
  // 목록
  const [datas, setDatas] = useState([]);
  // 제품 품질 결과 분석 정보
  const [inform, setInform] = useState({});
  // 로딩 여부
  const [isLoading, setIsLoading] = useState(true);
  // 메시지
  const [message, setMessage] = useState({});

  /* watch */
  useEffect(() => {
    // 모달이 표시된 경우
    if (show) {
      // 제품 품질 결과 분석 정보 조회 요청
      requestInform();
    }
  }, [show]);

  /* method */
  // 제품 품질 결과 분석 정보 조회 요청
  const requestInform = async () => {
    setDatas([]);
    setInform({});
    setIsLoading(true);
    setMessage({});

    try {
      const {
        data = {},
        code,
        message
      } = await requestAnalysisQualityProduct({
        reportYm,
        factoryCodeSeq,
        prdtCd,
        searchType: analysisType === 'NIR' ? 1 : 0
      });

      if (code !== RESPONSE_CODE.Success) {
        throw {
          code,
          error: message
        };
      }

      setDatas(data.list ?? []);
      setInform({
        mtrilGrpNm: data.mtrilGrpNm,
        sunjinStageNm: data.sunjinStageNm,
        prdtCd: data.prdtCd,
        prdtNm: data.prdtNm,
        mixCd: data.mixCd,
        mixNm: data.mixNm
      });
    } catch (error) {
      setMessage(error);
    } finally {
      setIsLoading(false);
    }
  };

  /* view */
  return (
    <Modal show={show} size="xl" onHide={onHide}>
      <Fragment key="header">{analysisType}</Fragment>
      <Fragment key="body">
        <List
          className={{
            table: 'mb-3',
            tbody: 'align-middle'
          }}
          columns={[
            'species',
            'sunjinStageNm',
            'prdtCd',
            'prdtNm',
            'mixCd',
            'mixNm'
          ].map(text => ({
            text,
            className: 'text-nowrap'
          }))}
          message={message}
          showLoading={isLoading}
        >
          <tr>
            <td>{inform.mtrilGrpNm || 'ㅡ'}</td>
            <td>{inform.sunjinStageNm || 'ㅡ'}</td>
            <td>{inform.prdtCd || 'ㅡ'}</td>
            <td>{inform.prdtNm || 'ㅡ'}</td>
            <td>{inform.mixCd || 'ㅡ'}</td>
            <td>{inform.mixNm || 'ㅡ'}</td>
          </tr>
        </List>
        <List
          className={{ tbody: 'align-middle' }}
          columns={[
            'inspReqDt',
            'moistureEn',
            'cproteinEn',
            'cfatEn',
            'cfiberEn',
            'ashEn',
            'caEn',
            'pEn',
            'isOver'
          ].map(text => ({
            text,
            className: 'text-nowrap'
          }))}
          message={message}
          showLoading={isLoading}
        >
          {datas.map(
            (
              {
                reqDt,
                moistureIsOver,
                moisture,
                proteinIsOver,
                protein,
                cFatIsOver,
                cFat,
                cFiberIsOver,
                cFiber,
                ashIsOver,
                ash,
                caIsOver,
                ca,
                pIsOver,
                p,
                isOver
              } = {},
              index
            ) => (
              <tr key={`datas${index}`}>
                <td
                  className={classNames({
                    'bg-soft-primary': index === datas.length - 1
                  })}
                >
                  {index === datas.length - 1 ? getText('avg') : reqDt || 'ㅡ'}
                </td>
                <td
                  className={classNames({
                    'bg-soft-primary': index === datas.length - 1,
                    'text-danger': moistureIsOver === 0
                  })}
                >
                  {comma(moisture) || 'ㅡ'}
                </td>
                <td
                  className={classNames({
                    'bg-soft-primary': index === datas.length - 1,
                    'text-danger': proteinIsOver === 0
                  })}
                >
                  {comma(protein) || 'ㅡ'}
                </td>
                <td
                  className={classNames({
                    'bg-soft-primary': index === datas.length - 1,
                    'text-danger': cFatIsOver === 0
                  })}
                >
                  {comma(cFat) || 'ㅡ'}
                </td>
                <td
                  className={classNames({
                    'bg-soft-primary': index === datas.length - 1,
                    'text-danger': cFiberIsOver === 0
                  })}
                >
                  {comma(cFiber) || 'ㅡ'}
                </td>
                <td
                  className={classNames({
                    'bg-soft-primary': index === datas.length - 1,
                    'text-danger': ashIsOver === 0
                  })}
                >
                  {comma(ash) || 'ㅡ'}
                </td>
                <td
                  className={classNames({
                    'bg-soft-primary': index === datas.length - 1,
                    'text-danger': caIsOver === 0
                  })}
                >
                  {comma(ca) || 'ㅡ'}
                </td>
                <td
                  className={classNames({
                    'bg-soft-primary': index === datas.length - 1,
                    'text-danger': pIsOver === 0
                  })}
                >
                  {comma(p) || 'ㅡ'}
                </td>
                <td
                  className={classNames({
                    'bg-soft-primary': index === datas.length - 1
                  })}
                >
                  {isOver || 'ㅡ'}
                </td>
              </tr>
            )
          )}
        </List>
      </Fragment>
      <Fragment key="footer">
        <Button onClick={onHide}>{getText('ok')}</Button>
      </Fragment>
    </Modal>
  );
};

Analysis.propTypes = {
  // 표시 여부
  show: PropTypes.bool,

  // 제품 코드
  prdtCd: PropTypes.string.isRequired,

  // 분석방법
  analysisType: PropTypes.string.isRequired,

  // 모달을 숨긴 경우 이벤트
  onHide: PropTypes.func
};
Analysis.defaultProps = { show: false };

export default Analysis;
